<script>
import BreezeAuthenticatedLayout from '@/Layouts/Authenticated.vue'
import {Head, Link} from '@inertiajs/vue3';
import Button from '@/Components/Button';
import Pagination from "@/Components/Pagination";
import Icon from "@/Components/Icon.vue";
import CheckMark from "@/Components/Icons/CheckMark.vue";
import XMark from "@/Components/Icons/XMark.vue";
import CheckOrXMark from "@/Components/Icons/CheckOrXMark.vue";
import CheckOrExclamationTriangle from "@/Components/Icons/CheckOrExclamationTriangle.vue";
import { router } from '@inertiajs/vue3'

export default {
    components: {
        CheckOrExclamationTriangle,
        CheckOrXMark,
        XMark,
        CheckMark,
        Icon,
        BreezeAuthenticatedLayout,
        Head,
        Link,
        Button,
        Pagination,
    },
    props: {
        users: Object,
        search: String,
    },
    data() {
        return {
            searchData: this.search,
            timerId: null,
        }
    },

    watch: {
        searchData: function (value) {
                this.updateQueryString(value);

                clearTimeout(this.timerId);

                this.timerId = setTimeout(() => {
                    router.reload({ only: ['users'] });
                }, 1000);
        }
    },

    methods: {
        resetInput() {
            this.search = "";
        },

        updateQueryString(searchValue) {
            const currentUrl = new URL(window.location);
            currentUrl.searchParams.set('search', searchValue);
            // everytime search is updated, we reset the page to 1
            currentUrl.searchParams.set('page', 1);
            window.history.pushState({}, '', currentUrl);
        }
    },
}
</script>

<style>
td {
    max-width: 100px; /* Limits the maximum width of each cell to 20% of the table width */
    white-space: normal;/* Prevents text from wrapping by default */
    text-overflow: ellipsis; /* Adds an ellipsis to indicate that the text has been truncated */
    overflow-wrap: break-word; /* Allows unbreakable words to be broken */
    word-wrap: break-word; /* Allow the breaking of long words and overflowed content */
    word-break: break-all; /* Allows unbreakable words to be broken */
}
td.td-username {
    max-width: 300px;
}

td.td-email {
    max-width: 300px;
}

</style>
<template>
    <Head title="Users" />

    <BreezeAuthenticatedLayout>
        <template #header>
            <div class="flex justify-between">
                <h2 class="font-semibold text-xl text-gray-800 leading-tight flex">
                    Users
                </h2>
                <div>
                    <Link :href="route('users.create')" class="btn mr-5">Create user</Link>
                    <Link :href="route('users.import')" class="btn mr-5">Import users</Link>
                    <a :href="route('users.export')" class="btn">Export users</a>
                </div>
            </div>
        </template>

        <div class="py-12">

            <div class="max-w-7xl mx-auto sm:px-6 lg:px-8 pb-4">
                <div class="block relative">
                    <input
                    type="text"
                    v-model="searchData"
                    class="py-4 px-5 w-full focus:ring-indigo-500 focus:border-indigo-500 block w-full shadow-sm border-gray-300 rounded-md"
                    placeholder="Search user..."
                    autofocus
                    />
                    <svg @click="resetInput" class="h-4 w-4 text-slate-300 absolute right-5 top-5 rounded-full cursor-pointer"  width="24" height="24" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor" fill="none" stroke-linecap="round" stroke-linejoin="round">  <path stroke="none" d="M0 0h24v24H0z"/>  <line x1="18" y1="6" x2="6" y2="18" />  <line x1="6" y1="6" x2="18" y2="18" /></svg>
                </div>
            </div>
            <div class="max-w-7xl mx-auto sm:px-6 lg:px-8">
                <div class="bg-white overflow-hidden shadow-sm sm:rounded-lg">
                    <div class="p-6 bg-white border-b border-gray-200">
                        <table class="w-full whitespace-nowrap">
                            <caption class="hidden">List of users</caption>
                            <thead>
                                <tr class="text-left font-bold">
                                    <th class="pb-4 pt-5 px-2 td-username" scope="col">Username</th>
                                    <th class="pb-4 pt-5 px-2 td-email" scope="col">Email</th>
                                    <th class="pb-4 pt-5 px-2" scope="col" title="Account Holder Code">A.H. Code</th>
                                    <th class="pb-4 pt-5 px-2" scope="col" title="Second Account Holder Code">Second A.H. Code</th>
                                    <th class="pb-4 pt-5 px-2" scope="col" title="The `Account Holder Active` flag means that `account_holder_code` is activated in the BGS and user can fetch data from the API.">A.H. Active</th>
                                    <th class="pb-4 pt-5 px-2" scope="col">Brand</th>
                                    <th class="pb-4 pt-5 px-2" scope="col" title="The `Active` flag means whether the user is allowed to login or not on the portal.">Active</th>
                                    <th class="pb-4 pt-5 px-2" scope="col" title="Account created at date">Created</th>
                                    <th class="pb-4 pt-5 px-2" scope="col" title="Account last login date">Last Login</th>
                                    <th class="pb-4 pt-5 px-2" scope="col" title="Is account an admin">Is admin</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="user in users.data" :key="user.id" :href="route('users.show', {user: user.id})" class="hover:bg-gray-100 focus-within:bg-gray-100">
                                    <td class="border-t py-2 td-username">
                                        <Link class="flex items-center px-2" :href="route('users.show', {user: user.id})" tabindex="-1">
                                            {{ user.name }}
                                        </Link>
                                    </td>
                                    <td class="border-t py-2 td-email">
                                        <Link class="flex items-center px-2" :href="route('users.show', {user: user.id})" tabindex="-1">
                                            {{ user.email }}
                                        </Link>
                                    </td>
                                    <td class="border-t py-2">
                                        <Link class="flex items-center px-2" :href="route('users.show', {user: user.id})" tabindex="-1">
                                            {{ user.profile.account_holder_code }}
                                        </Link>
                                    </td>
                                    <td class="border-t py-2">
                                        <Link class="flex items-center px-2" :href="route('users.show', {user: user.id})" tabindex="-1">
                                            {{ user.profile.second_account_holder_code }}
                                        </Link>
                                    </td>
                                    <td class="border-t py-2">
                                        <Link class="flex items-center px-2" :href="route('users.show', {user: user.id})" tabindex="-1">
                                            <CheckOrXMark :condition="user.profile.account_holder_active" />
                                        </Link>
                                    </td>
                                    <td class="border-t py-2">
                                        <Link class="flex items-center px-2" :href="route('users.show', {user: user.id})" tabindex="-1">
                                            {{ user.brand_slug }}
                                        </Link>
                                    </td>
                                    <td class="border-t py-2">
                                        <Link class="flex items-center px-2" :href="route('users.show', {user: user.id})" tabindex="-1">
                                            <CheckOrXMark :condition="user.active" />
                                        </Link>
                                    </td>
                                    <td class="w-px border-t py-2">
                                        <Link class="flex items-center px-2" :href="route('users.show', {user: user.id})" tabindex="-1">
                                            {{ formatDate(user.created_at) }}
                                        </Link>
                                    </td>
                                    <td class="w-px border-t py-2">
                                        <Link class="flex items-center px-2" :href="route('users.show', {user: user.id})" tabindex="-1">
                                            {{ formatDate(user.last_login_at) }}
                                        </Link>
                                    </td>
                                    <td class="w-px border-t py-2">
                                        <Link class="flex items-center px-2" :href="route('users.show', {user: user.id})" tabindex="-1">
                                            <CheckOrXMark :condition="user.is_admin_bool" />
                                        </Link>
                                    </td>

                                </tr>
                                <tr v-if="users.data.length === 0">
                                    <td class="px-6 py-4 border-t" colspan="4">No Users found.</td>
                                </tr>
                            </tbody>
                        </table>

                        <pagination class="mt-6" :links="users.links" />

                    </div>
                </div>
            </div>
        </div>
    </BreezeAuthenticatedLayout>
</template>
